import { useCallback, useState, useMemo, useEffect, useContext } from "react";
import { useDropzone } from "react-dropzone";
import { Document, Page, pdfjs, Thumbnail } from 'react-pdf';

import ModalTemplate from "../../../../common/ModalTemplate";
import { timestampToDate } from "../../../../utils";
import { deleteCertificate, fetchDocuments, uploadDocuments } from "../../../service";
import { showNotification } from "../../../../common/notifications/notifications";
import styles from "./styles";
import DivSpinner from "../../../../common/DivSpinner";
import { Context } from "../../../Provider";
import BtnSpinner from "../../../../common/BtnSpinner";
import { theme } from "../../../../theme";

// Update PDF.js worker configuration
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FileAndUploader = ({ ftm_id, show, handleClose, item, items, parentRefresh, handleNextItem, handlePrevItem }) => {
    const index = items.findIndex(t => t.documents_types_id === item?.documents_types_id);
    const title = `Upload ${item?.documents_types_name} certificates (${index + 1} of ${items.length})`;

    const { state } = useContext(Context);
    const isCompletedRegistration = state.isCompletedRegistration;

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nav, setNav] = useState({ next: null, prev: null });

    // New states for PDF viewer modal
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerNumPages, setViewerNumPages] = useState(null);
    const [viewerPageNumber, setViewerPageNumber] = useState(1);

    const onViewerDocumentLoadSuccess = ({ numPages }) => {
        setViewerNumPages(numPages);
        setViewerPageNumber(1);
    };

    // Function to open viewer modal
    const openViewer = (file) => {
        setSelectedFile(file);
        setViewerOpen(true);
        setViewerPageNumber(1);
    };

    // Function to close viewer modal
    const closeViewer = () => {
        setViewerOpen(false);
        setSelectedFile(null);
        setViewerNumPages(null);
        setViewerPageNumber(1);
    };

    // Navigation functions for PDF viewer
    const goToPrevPage = () => {
        setViewerPageNumber(prev => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setViewerPageNumber(prev => Math.min(viewerNumPages, prev + 1));
    };

    useEffect(() => {
        setNav({
            prev: index > 0 ? items[index - 1] : null,
            next: index < items.length - 1 ? items[index + 1] : null
        });
    }, [index, item?.documents_types_id, items]);

    useEffect(() => {
        const getCertificates = async () => {
            setLoading(true);
            try {
                const response = await fetchDocuments(ftm_id, item?.documents_types_id);
                if (response?.status === "Success") {
                    const newDocuments = response?.documents?.ftm_document_files || [];

                    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

                    const mappedFiles = newDocuments.map((doc, index) => {
                        const fileName = doc.ftm_document_file_path.split('/').pop();
                        const isImage = imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));

                        return {
                            id: `${Date.now()}-${index}`,
                            file: null,
                            preview: doc.ftm_document_file_path,
                            fileName,
                            isImage,
                            status: "success",
                            ftm_document_file_id: doc.ftm_document_file_id,
                            type: 'EXISTING',
                            dateTime: doc.ftm_document_file_uploaded_at
                        };
                    });

                    setFiles(mappedFiles);
                } else {
                    setFiles([]);
                }
            } catch (error) {
                console.error("Error fetching documents:", error);
                setFiles([]);
            } finally {
                setLoading(false);
            }
        };

        getCertificates();
    }, [ftm_id, item?.documents_types_id]);

    const hasUploadingFiles = useMemo(
        () => files.some(f => (f.status !== "success" && f.status !== "error")),
        [files]
    );

    const updateFileStatus = useCallback((id, updates) => {
        setFiles(prev =>
            prev.map(f => (f.id === id ? { ...f, ...updates } : f))
        );
    }, []);

    const uploadFile = useCallback(async (fileObj) => {
        updateFileStatus(fileObj.id, { status: "uploading" });

        try {
            const response = await uploadDocuments({
                ftm_id,
                ftm_document_type_id: item?.documents_types_id,
                ftm_document_type: item?.documents_types_name,
                document_type: item?.documents_types_type,
                ftm_document: fileObj.file,
            });

            const updates = response?.status === "Success"
                ? {
                    status: "success",
                    ftm_document_file_id: response.ftm_document_file_id,
                    document_path: response.document_path,
                }
                : { status: "error" };

            updateFileStatus(fileObj.id, updates);
            parentRefresh();
        } catch {
            updateFileStatus(fileObj.id, { status: "error" });
        }
    }, [ftm_id, item, updateFileStatus, parentRefresh]);

    const onDrop = useCallback(async (acceptedFiles) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

        // Build file objects
        const fileObjs = acceptedFiles.map((file, index) => {
            const id = `${Date.now()}-${index}`;
            const fileName = file.name;
            const isImage = imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));

            return {
                id,
                file,
                preview: URL.createObjectURL(file),
                fileName,
                isImage,
                status: "pending",
                type: 'NEW',
                dateTime: Date.now()
            };
        });

        // Show all files instantly in the UI
        setFiles(prev => [...fileObjs, ...prev]);

        // Upload sequentially (one-by-one)
        for (const fileObj of fileObjs) {
            await uploadFile(fileObj);
        }
    }, [uploadFile]);


    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': [],
            'application/pdf': [],
        },
        multiple: true,
    });

    const revokePreview = (id) => {
        const file = files.find(f => f.id === id);
        if (file?.preview) URL.revokeObjectURL(file.preview);
    };

    const removeFile = (file) => {
        const { id, status, ftm_document_file_id } = file;

        if (status === "error") {
            revokePreview(id);
            setFiles(prev => prev.filter(f => f.id !== id));
        } else {
            showNotification({
                notificationType: "UPLOAD_IMAGE_DELETE",
                preConfirm: () => {
                    updateFileStatus(id, { status: "uploading" });
                    return deleteCertificate(ftm_document_file_id);
                },
                isConfirmed: (result) => {
                    if (result?.isConfirmed && result?.value?.status === "Success") {
                        revokePreview(id);
                        setFiles(prev => prev.filter(f => f.id !== id));
                    } else {
                        updateFileStatus(id, { status: "success" }); // rollback
                    }
                },
            });
        }
    };

    const LoadingContent = () => (
        <div style={styles.fileLoading}>
            <i className="material-icons" style={{ fontSize: "64px", color: "#ccc" }}>
                cloud_download
            </i>
            <p style={{ fontSize: "14px", color: "#777" }}>File loading...</p>
        </div>
    );

    const hasFiles = useMemo(() => files.length > 0, [files]);

    const goNext = async (types_id) => {
        if (hasUploadingFiles) return;
        handleNextItem(types_id);
    };

    const goPrev = async (types_id) => {
        if (hasUploadingFiles) return;
        handlePrevItem(types_id);
    };

    return (
        <>
            <ModalTemplate
                show={show}
                handleClose={handleClose}
                // size="xl"
                centered
                title={title}
                backdrop="static"
                body={
                    <section>
                        {!isCompletedRegistration && (
                            <div {...getRootProps({ style: styles.dropzone })}>
                                <input {...getInputProps()} />
                                <p className="font-weight-bold mt-3">
                                    {isDragActive ? "Drop the files here..." : "Drag & drop files or click to select"}
                                </p>
                            </div>
                        )}
                        {loading ? (
                            <LoadingContent />
                        ) : (
                            <>
                                {hasFiles ? (
                                    <aside
                                        style={{
                                            ...styles.thumbsContainer,
                                            justifyContent: window.innerWidth < 770 ? "center" : "",
                                            height: files.length >= 14 ? "450px" : "",
                                            overflow: files.length >= 14 ? "auto" : ""
                                        }}
                                    >
                                        {files.map(file => (
                                            <div key={file.id} style={styles.thumb}>
                                                {(file?.status === "success" || file?.status === "error") && (
                                                    <button
                                                        onClick={() => removeFile(file)}
                                                        style={{
                                                            ...styles.topRightRemoveBtn,
                                                            zIndex: 10
                                                        }}
                                                        title="Remove"
                                                    >
                                                        &times;
                                                    </button>
                                                )}

                                                <div
                                                    onClick={() => file.status === "success" && openViewer(file)}
                                                    style={{
                                                        cursor: file.status === "success" ? "pointer" : "default",
                                                        position: "relative",
                                                        overflow: "hidden",
                                                        width: "100%",
                                                        height: "150px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >

                                                    {file?.isImage ? (
                                                        <img
                                                            src={file.preview}
                                                            alt={file.fileName}
                                                            style={styles.thumbImage}
                                                        />
                                                    ) : (
                                                        <Document
                                                            file={file.type === 'NEW' ? file?.file : file?.preview}
                                                        >
                                                            <Thumbnail pageNumber={1} width={100} />
                                                        </Document>
                                                    )}
                                                </div>

                                                <p className="mt-0 mb-0 font-weight-bold" style={styles.fileName}>
                                                    {timestampToDate(file?.dateTime, true)}
                                                </p>

                                                <p className="font-weight-bold mb-0 pb-0" style={styles.status}>
                                                    {file?.type === "NEW" ? (
                                                        <>
                                                            {(file?.status !== "success" && file?.status !== "error") && (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                                                    <i className="material-icons text-warning">upload</i>
                                                                    <span className="text-sm">UPLOADING...</span>
                                                                </div>
                                                            )}
                                                            {file.status === "success" && (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                                                    <i className="material-icons text-success">check_circle</i>
                                                                    <span className="text-sm">UPLOADED</span>
                                                                </div>
                                                            )}
                                                            {file.status === "error" && (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                                                    <i className="material-icons text-danger">error</i>
                                                                    <span className="text-sm">ERROR</span>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                                            <i className="material-icons text-success">check_circle</i>
                                                            <span className="text-sm">UPLOADED</span>
                                                        </div>
                                                    )}
                                                </p>
                                            </div>
                                        ))}
                                    </aside>
                                ) : (
                                    <div style={styles.emptyState}>
                                        <i className="material-icons" style={{ fontSize: "64px", color: "#ccc" }}>
                                            folder_open
                                        </i>
                                        <p style={{ fontSize: "14px", color: "#777" }}>No files uploaded yet</p>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                }
                footer={
                    <div className="w-100">
                        <div className={`d-flex align-items-center ${nav.prev ? "justify-content-between" : nav.prev ? "justify-content-start" : "justify-content-end"} my-2 `}>
                            {nav.prev && (
                                <div
                                    className={`d-flex align-items-center gap-1 ${hasUploadingFiles ? "text-muted" : "cursor-pointer"}`}
                                    style={{ opacity: hasUploadingFiles ? 0.6 : 1 }}
                                >
                                    <button
                                        className={`btn btn-${theme.main} btn-sm w-100`}
                                        onClick={() => !hasUploadingFiles && goPrev(item?.documents_types_id)}
                                    >
                                        <i className="material-icons text-lg">arrow_back</i>
                                        {hasUploadingFiles
                                            ? <BtnSpinner />
                                            : nav.prev
                                                ? "Previous"
                                                : "Done"}
                                    </button>
                                </div>
                            )}

                            <div
                                className={`d-flex align-items-center gap-1 ${hasUploadingFiles ? "text-muted" : "cursor-pointer"}`}
                                style={{ opacity: hasUploadingFiles ? 0.6 : 1 }}
                            >
                                <button
                                    className={`btn btn-${theme.main} btn-sm w-100`}
                                    onClick={() => {
                                        if (!hasUploadingFiles) {
                                            nav.next ? goNext(item?.documents_types_id) : handleClose();
                                        }
                                    }}
                                >
                                    {hasUploadingFiles
                                        ? <BtnSpinner />
                                        : nav.next
                                            ? "Next"
                                            : "Done"}
                                    <i className="material-icons text-lg">{nav.next ? "arrow_forward" : "check"}</i>
                                </button>
                            </div>
                        </div>
                    </div>
                }
            />

            {/* PDF/Image Viewer Modal */}
            {viewerOpen && selectedFile && (
                <ModalTemplate
                    show={viewerOpen}
                    handleClose={closeViewer}
                    // size="lg"
                    centered
                    title={selectedFile.fileName}
                    backdrop="static"
                    body={
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            maxHeight: '70vh',
                            overflow: 'auto'
                        }}>
                            {selectedFile.isImage ? (
                                <img
                                    src={selectedFile.preview}
                                    alt={selectedFile.fileName}
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <Document
                                    file={selectedFile.type === 'NEW' ? selectedFile.file : selectedFile.preview}
                                    onLoadSuccess={onViewerDocumentLoadSuccess}
                                    loading={<DivSpinner />}
                                >
                                    <Page
                                        pageNumber={viewerPageNumber}
                                        width={750}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                    />
                                </Document>
                            )}
                        </div>
                    }
                    footer={
                        !selectedFile.isImage && viewerNumPages && (
                            <div className="w-100 d-flex justify-content-between align-items-center">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={goToPrevPage}
                                    disabled={viewerPageNumber <= 1}
                                >
                                    <i className="material-icons">arrow_back</i> Previous
                                </button>

                                <span className="font-weight-bold">
                                    Page {viewerPageNumber} of {viewerNumPages}
                                </span>

                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={goToNextPage}
                                    disabled={viewerPageNumber >= viewerNumPages}
                                >
                                    Next <i className="material-icons">arrow_forward</i>
                                </button>
                            </div>
                        )
                    }
                />
            )}
        </>
    );
};

export default FileAndUploader;